/**
 * Loading and reloading for the URL Tracking screens.
 *
 * These pages are the one part of the workspace whose numbers move without
 * anybody in this tab doing anything — a customer taps a link on the other
 * side of the world and the count goes up. So none of it lives in the app
 * store beside clients and deals, which are loaded once and edited locally.
 * Each screen asks the server, and the parts that tick ask again.
 *
 * `changed()` is how a sheet tells the list behind it to refetch. The
 * alternative was threading a callback from every page through setSheet and
 * into each editor, which is a lot of plumbing to say one thing.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import api from './api.js';

const listeners = new Set();

/** Something was created, edited or archived — anything showing it should reload. */
export const changed = () => listeners.forEach(fn => fn());

/**
 * Fetch on mount, again when `deps` change, and again whenever anything in
 * the module is saved.
 *
 * Answers are dropped if they arrive after the component has gone or after a
 * newer request was made — filters change faster than the network answers,
 * and without this a slow reply for "last month" can land on top of a fast
 * one for "today" and show the wrong figures under the right filter.
 */
export function useTracking(load, deps = []) {
  const [state, setState] = useState({ data: null, error: '', loading: true });
  const alive = useRef(true);
  const run = useRef(0);

  const reload = useCallback((quiet = false) => {
    const mine = ++run.current;
    if (!quiet) setState(s => ({ ...s, loading: true, error: '' }));

    return Promise.resolve()
      .then(load)
      .then(data => {
        if (!alive.current || mine !== run.current) return;
        setState({ data, error: '', loading: false });
      })
      .catch(e => {
        if (!alive.current || mine !== run.current) return;
        setState(s => ({ data: s.data, error: e.message || 'Could not load', loading: false }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    alive.current = true;
    reload();
    return () => { alive.current = false; };
  }, [reload]);

  useEffect(() => {
    const fn = () => reload(true);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, [reload]);

  return { ...state, reload };
}

/**
 * Poll while the tab is in front.
 *
 * A background tab is not being watched, and polling one is a request every
 * few seconds that nobody will ever see. This stops when the tab is hidden and
 * fires once immediately on return, so coming back shows current numbers
 * rather than whatever was on screen when you left.
 */
export function usePoll(fn, ms, on = true) {
  const latest = useRef(fn);
  latest.current = fn;

  useEffect(() => {
    if (!on || !ms) return undefined;
    let timer = null;

    const tick = () => { if (!document.hidden) latest.current(); };
    const start = () => { clearInterval(timer); timer = setInterval(tick, ms); };
    const onVisible = () => { if (!document.hidden) { tick(); start(); } };

    start();
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [ms, on]);
}

/* ------------------------------------------------------------ live clicks */

/**
 * Watch clicks arrive, by stream where possible and by polling where not.
 *
 * The stream is the honest way to do this: the server already knows the moment
 * a click is recorded, and asking it every few seconds whether anything has
 * happened is a worse version of being told. But a stream can fail for reasons
 * that have nothing to do with this app — a proxy that buffers, a corporate
 * middlebox, a browser without EventSource — and a dashboard that silently
 * stops updating is worse than one that quietly polls. So it falls back, and
 * says which mode it is in rather than pretending they are the same.
 *
 * `onCounters` receives a whole snapshot from the database; `onClick` receives
 * one click as it lands. The caller applies the click optimistically and lets
 * the next snapshot correct it — which it will, every ten seconds, including
 * the unique-visitor count that no single click could tell you about.
 */
export function useLiveClicks({ onCounters, onClick, enabled = true, pollMs = 4000 }) {
  const [mode, setMode] = useState('connecting');
  const handlers = useRef({ onCounters, onClick });
  handlers.current = { onCounters, onClick };

  useEffect(() => {
    if (!enabled) { setMode('off'); return undefined; }

    let source = null;
    let poller = null;
    let dropped = false;

    const poll = () => {
      if (document.hidden) return;
      api.urlTracking.analytics.live()
        .then(d => handlers.current.onCounters?.(d))
        .catch(() => {});
    };

    const fallBack = () => {
      if (dropped) return;
      setMode('poll');
      clearInterval(poller);
      poll();
      poller = setInterval(poll, pollMs);
    };

    api.urlTracking.streamTicket()
      .then(({ ticket }) => {
        if (dropped) return;
        if (typeof EventSource === 'undefined') { fallBack(); return; }

        source = new EventSource(api.urlTracking.streamUrl(ticket));

        source.addEventListener('hello', () => setMode('stream'));
        source.addEventListener('counters', e => {
          setMode('stream');
          try { handlers.current.onCounters?.(JSON.parse(e.data)); } catch { /* malformed frame */ }
        });
        source.addEventListener('click', e => {
          try { handlers.current.onClick?.(JSON.parse(e.data)); } catch { /* malformed frame */ }
        });

        /*
         * EventSource reconnects on its own, and its ticket is already spent
         * by then — the reconnection arrives without a valid one and is
         * refused. Rather than let it retry against a door that will not open,
         * the stream is closed here and polling takes over; the next time this
         * screen mounts it will ask for a fresh ticket and try again.
         */
        source.onerror = () => {
          if (source.readyState === EventSource.CLOSED || source.readyState === EventSource.CONNECTING) {
            source.close();
            source = null;
            fallBack();
          }
        };
      })
      .catch(fallBack);

    return () => {
      dropped = true;
      clearInterval(poller);
      if (source) source.close();
    };
  }, [enabled, pollMs]);

  return mode;
}

/* ---------------------------------------------------------------- copying */

/**
 * Copy, with a fallback for the browsers and contexts that refuse the
 * clipboard API — it needs a secure origin, and a workspace served over plain
 * http on a local network is not one. Without the fallback, Copy silently does
 * nothing exactly where people are most likely to be testing.
 */
export async function copy(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through to the old way */ }

  try {
    const box = document.createElement('textarea');
    box.value = text;
    box.setAttribute('readonly', '');
    box.style.cssText = 'position:fixed;top:-1000px;opacity:0';
    document.body.appendChild(box);
    box.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(box);
    return ok;
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------ downloading */

/**
 * Hand the browser a file.
 *
 * The export endpoints need the bearer token, so they cannot be a plain link —
 * the bytes are fetched with the session and turned into a file here. The
 * object URL is revoked on the next tick: too early and Firefox cancels the
 * download, never and the blob is held for the life of the tab.
 */
export function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* --------------------------------------------------------------- building */

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

/**
 * The destination with its UTM tags on it, for the live preview.
 *
 * Built the same way the server builds it — through URLSearchParams, never by
 * joining strings — so that what is previewed is what will be stored. Returns
 * the raw text unchanged while it is still being typed and is not yet a URL.
 */
export function previewUrl(destination, utm) {
  const raw = String(destination || '').trim();
  if (!raw) return '';
  let url;
  try {
    url = new URL(raw);
  } catch {
    return raw;
  }
  for (const key of UTM_KEYS) {
    const v = String((utm && utm[key]) || '').trim();
    if (v) url.searchParams.set(key, v);
    else url.searchParams.delete(key);
  }
  return url.toString();
}

/** Is this something the server will accept as a destination? */
export function validDestination(value) {
  const raw = String(value || '').trim();
  if (!raw) return false;
  try {
    const u = new URL(raw);
    return (u.protocol === 'http:' || u.protocol === 'https:') && !!u.hostname;
  } catch {
    return false;
  }
}

/**
 * What people actually type into a UTM field.
 *
 * Lowercase, spaces to underscores. Analytics tools treat utm_source=WhatsApp
 * and utm_source=whatsapp as two different sources, so a campaign tagged
 * inconsistently reports itself as two — this is only applied to the preset
 * buttons, never to what somebody has typed by hand, because §8 is explicit
 * that a custom value must be possible.
 */
export const slug = v => String(v || '').trim().toLowerCase().replace(/\s+/g, '_');

/** A short, readable form of a long destination, for table cells. */
export function shortenUrl(value, max = 44) {
  const raw = String(value || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
  return raw.length > max ? `${raw.slice(0, max - 1)}…` : raw;
}

export default {
  changed, useTracking, usePoll, useLiveClicks, copy, download,
  previewUrl, validDestination, slug, shortenUrl
};
